import React, { useState, useMemo } from "react";
import { Box, Typography, Popover, IconButton, Divider } from "@mui/material";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";

/**
 * DateRangeFilter
 * 
 * Performance: Matches project filter styles (Radius: 8px, Font: 12px, BG: #f8fafc).
 */
const DateRangeFilter = ({ value = { start: "", end: "" }, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ start: "", end: "" });
  };

  const handleStartChange = (newValue) => {
    const startStr = newValue ? newValue.format("YYYY-MM-DD") : "";
    onChange({
      start: startStr,
      end: value.end,
    });
  };

  const handleEndChange = (newValue) => {
    const endStr = newValue ? newValue.format("YYYY-MM-DD") : "";
    onChange({
      start: value.start,
      end: endStr,
    });
    if (value.start) handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const startDisplay = useMemo(() =>
    value.start ? dayjs(value.start).format("DD/MM/YYYY") : "DD/MM/YYYY",
    [value.start]);

  const endDisplay = useMemo(() =>
    value.end ? dayjs(value.end).format("DD/MM/YYYY") : "DD/MM/YYYY",
    [value.end]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          height: "28px", // Reduced from 40px to match project standard
          minWidth: "280px",
          backgroundColor: "#ffffff",
          cursor: "pointer",
          px: "12px",
          fontFamily: "var(--font-primary)",
          transition: "all 0.2s ease",
          "&:hover": { borderColor: "#cbd5e1" },
          ...(open && {
            borderColor: "#d32f2f",
            backgroundColor: "#fff",
          })
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Start Segment */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#64748b",
              fontFamily: "var(--font-primary)",
              textTransform: "uppercase"
            }}>Start:</Typography>
            <Typography sx={{
              fontSize: "12px",
              color: value.start ? "#1e293b" : "#94a3b8",
              fontWeight: 400,
              fontFamily: "var(--font-primary)"
            }}>
              {startDisplay}
            </Typography>
          </Box>

          <Box sx={{ height: "14px", width: "1px", backgroundColor: "#e2e8f0", mx: 0.5 }} />

          {/* End Segment */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#64748b",
              fontFamily: "var(--font-primary)",
              textTransform: "uppercase"
            }}>End:</Typography>
            <Typography sx={{
              fontSize: "12px",
              color: value.end ? "#1e293b" : "#94a3b8",
              fontWeight: 400,
              fontFamily: "var(--font-primary)"
            }}>
              {endDisplay}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {(value.start || value.end) && (
            <IconButton size="small" onClick={handleClear} sx={{ p: 0.25, color: "#94a3b8", "&:hover": { color: "#d32f2f" } }}>
              <FaTimes size={11} />
            </IconButton>
          )}
          <FaCalendarAlt color="#64748b" size={12} style={{ marginLeft: "8px" }} />
        </Box>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              border: "1px solid #f1f5f9",
              p: 2
            }
          }
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ px: 2, pb: 0.5, display: "block", fontWeight: 700, color: "#64748b", fontFamily: "var(--font-primary)" }}>
              START DATE
            </Typography>
            <DateCalendar
              value={value.start ? dayjs(value.start) : null}
              onChange={handleStartChange}
              maxDate={value.end ? dayjs(value.end) : undefined}
              sx={{ m: 0 }}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box>
            <Typography variant="caption" sx={{ px: 2, pb: 0.5, display: "block", fontWeight: 700, color: "#64748b", fontFamily: "var(--font-primary)" }}>
              END DATE
            </Typography>
            <DateCalendar
              value={value.end ? dayjs(value.end) : null}
              onChange={handleEndChange}
              minDate={value.start ? dayjs(value.start) : undefined}
              sx={{ m: 0 }}
            />
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;
